import ROUTES, { AdvisoryManagePage, DisposalManagePage, RequirementManagePage } from '@agency/routes/routes.constant';
import { BackUrlResult } from '@shared/hooks/useBackUrl';
import { SavedViewType } from '@shared/models/saved_view/savedView';
import {
  AvailabilityScheduleStatuses,
  InterestScheduleStatuses,
  RequirementMatchedSpace,
  SCHEDULE_MATCH_ALL_GROUP,
  ScheduleMatchStatusGroups,
} from '@shared/types/common/match';
import { getBaseUrl } from '@shared/utils/base-url';
import { getQueriesAsSearch } from '@shared/utils/common';

// ---- Utils ----

export const getUrlWithBack = (url: string, backUrl: BackUrlResult, query?: Query) => {
  return backUrl.getUrlWithBackUrlParam(url, query);
};

// ---- New ----

export const getDisposalManagePage = (id: number, page: DisposalManagePage, backUrl = '', routeParams?: ParsedQuery) =>
  `${ROUTES.organisation.disposals.root}/${id}${page}${getQueriesAsSearch({
    k_back: encodeURIComponent(backUrl),
    ...routeParams,
  })}`;

export const getSocietyDisposalsUrl = (params?: PartialQuery) =>
  `${ROUTES.marketplace.disposals}${getQueriesAsSearch({ ...params })}`;

export const getCrmCompaniesUrl = (params?: PartialQuery) =>
  `${ROUTES.organisation.crm.root}${ROUTES.organisation.crm.companies}${getQueriesAsSearch({ ...params })}`;

export const getCrmContactsUrl = (params?: PartialQuery) =>
  `${ROUTES.organisation.crm.root}${ROUTES.organisation.crm.contacts}${getQueriesAsSearch({ ...params })}`;

export const getSocietyAcquisitionsUrl = (params?: PartialQuery) =>
  `${ROUTES.marketplace.requirements}${getQueriesAsSearch({ ...params })}`;

export const getScheduleInvitesUrl = (params?: PartialQuery) =>
  `${ROUTES.organisation.scheduleInvitees.root}${getQueriesAsSearch({ ...params })}`;

export const getRequirementManagePage = (
  id: number,
  page: RequirementManagePage,
  backUrl = '',
  routeParams?: ParsedQuery
) => {
  return `${ROUTES.organisation.requirements.root}/${id}${page}${getQueriesAsSearch({
    ...(!!backUrl && { k_back: encodeURIComponent(backUrl) }),
    ...routeParams,
  })}`;
};

export const getAdvisoryManagePage = (
  id: number,
  page: AdvisoryManagePage,
  backUrl = '',
  routeParams?: ParsedQuery
) => {
  return `${ROUTES.organisation.advisory.root}/${id}${page}${getQueriesAsSearch({
    k_back: encodeURIComponent(backUrl),
    ...routeParams,
  })}`;
};

export const getMatchedSpaceUrl = (matchedSpace: RequirementMatchedSpace): string => {
  if (!matchedSpace.disposal_context && !matchedSpace.requirement_context) {
    throw new Error('Matched space must have either disposal or requirement context');
  }

  // ---- Availability Schedule ----

  if (matchedSpace.requirement_context) {
    const tableTab =
      matchedSpace.shortlist_status === AvailabilityScheduleStatuses.Inbound
        ? ScheduleMatchStatusGroups.MatchRequests
        : SCHEDULE_MATCH_ALL_GROUP;

    return getRequirementManagePage(
      matchedSpace.requirement_id,
      ROUTES.organisation.requirements.manage.availability_schedule,
      '',
      { table_tab: tableTab, view: SavedViewType.list, view_match_id: matchedSpace.id.toString() }
    );
  }

  // ---- Interest Schedule ----

  const params: Query = {};

  if (matchedSpace.shortlist_status === InterestScheduleStatuses.Outbound) {
    params.space = 'outbound';
    params.threadId = matchedSpace.id.toString();
  } else if (matchedSpace.shortlist_status === InterestScheduleStatuses.Inbound) {
    params.space = 'inbound';
    params.threadId = matchedSpace.id.toString();
  } else {
    params.space = 'schedule';
    params.view_interest = matchedSpace.id.toString();
  }

  const disposalId = matchedSpace.disposal_id.toString();
  return ROUTES.organisation.disposals.manage.reportingWithParams(disposalId, params);
};

// ---- Legacy ----

export const getLegacyDisposalManageUrl = (id: string, path: string) => {
  return `${getBaseUrl()}#/disposals/${id}${path}`;
};

export const getLegacyRequirementIndexUrl = (id?: string) => {
  if (id) {
    return `${getBaseUrl()}#/requirements/list/all/${id}`;
  }

  return `${getBaseUrl()}#/requirements`;
};

export const getLegacyRequirementManageUrl = (id: string, path: string) => {
  return `${getBaseUrl()}#/requirements/${id}/manage${path}`;
};
export const getLegacyRequirementFindMatchesUrl = (id: string, path: string) => {
  return getLegacyRequirementManageUrl(id, `/search${path}`);
};

export const getLegacyLeaseTransactionManageUrl = (id: string, path: string) => {
  return `${getBaseUrl()}#/leases/transactions/${id}/manage${path}`;
};

export const getLegacySaleTransactionManageUrl = (id: string, path: string) => {
  return `${getBaseUrl()}#/sales/transactions/${id}/manage${path}`;
};

export const getLegacyViewingIndexUrl = (id?: string) => {
  if (id) {
    return `${getBaseUrl()}#/viewings/task/${id}`;
  }

  return `${getBaseUrl()}#/viewings`;
};

export const getDisposalRoute = (disposalId: number) => {
  const disposalRoute = getDisposalManagePage(disposalId, ROUTES.organisation.disposals.manage.summary);

  function shouldIgnoreClick(e: React.MouseEvent<HTMLElement>): boolean {
    const ignore = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;

    if (ignore) return true;

    e.stopPropagation();
    e.preventDefault();
    return false;
  }

  return {
    route: disposalRoute,
    shouldIgnoreClick,
  };
};
